import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider, useNotifications } from './contexts/NotificationContext';
import { TutorialProvider } from './contexts/TutorialContext';
import LoginForm from './components/LoginForm';
import RegisterForm from './components/RegisterForm';
import ForgotPasswordForm from './components/ForgotPasswordForm';
import ResetPasswordForm from './components/ResetPasswordForm';
import Home from './components/Home';
import NotificationToast from './components/NotificationToast';
import TutorialOverlay from './components/TutorialOverlay';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';

const AuthenticatedApp: React.FC = () => {
  const { user, isLoading } = useAuth();
  const { notifications, dismissNotification } = useNotifications();
  const [showRegister, setShowRegister] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  if (isLoading) {
    return <div className="loading">Loading...</div>;
  }

  // If there's a token parameter, show reset password form
  if (token && !user) {
    return (
      <div className="auth-container">
        <ResetPasswordForm token={token} />
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <NotificationToast notifications={notifications} onDismiss={dismissNotification} />
        <div className="auth-container">
          {showForgotPassword ? (
            <ForgotPasswordForm onBackToLogin={() => setShowForgotPassword(false)} />
          ) : showRegister ? (
            <RegisterForm onSwitchToLogin={() => setShowRegister(false)} />
          ) : (
            <LoginForm
              onSwitchToRegister={() => setShowRegister(true)}
              onForgotPassword={() => setShowForgotPassword(true)}
            />
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <NotificationToast notifications={notifications} onDismiss={dismissNotification} />
      <TutorialOverlay />
      <Home />
    </>
  );
};

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  return (
    <div className="auth-container">
      <ResetPasswordForm token={token} />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <NotificationProvider>
          <TutorialProvider>
            <Router>
              <div className="App">
                <Routes>
                  <Route path="/" element={<AuthenticatedApp />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </div>
            </Router>
          </TutorialProvider>
        </NotificationProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;